import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FolderOpen, ArrowLeft, MessageSquare, Download, Pencil, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { WorkExtras } from "@/components/WorkExtras";

const KIND = { ebook: "Ebook", outline: "Outline", draft: "Draft", plan: "Plan", other: "Project", sprint: "7-Day Sprint" };
const label = (w) => w.kind === "sprint" ? (w.active ? "ACTIVE SPRINT" : "Saved sprint") : (KIND[w.kind] || "Project");
const md = "text-sm sm:text-base text-gray-100 whitespace-pre-wrap leading-relaxed";
const clean = (t) => (t || "").replace(/\*\*(.+?)\*\*/g, "$1").replace(/^#{1,6}\s*/gm, "");

const WorkList = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/works").then((r) => {
      if (!alive) return;
      setData(r.data);
      if (r.data.backfill === "running") setTimeout(load, 4000);
    }).catch(() => alive && setData({ works: [] }));
    load();
    return () => { alive = false; };
  }, []);

  if (!data) return <p className="mt-6 text-gray-400 text-sm">Loading your work...</p>;
  return (
    <>
      {data.backfill === "running" && (
        <p className="mt-4 text-xs text-gray-400" data-testid="work-backfill">Gathering work from your past coach chats...</p>
      )}
      {data.works.length === 0 ? (
        <div className="mt-6 card-panel p-6 text-center" data-testid="work-empty">
          <p className="text-gray-300">Nothing saved yet. Ask your coach to write an ebook chapter, outline, plan or draft, and it lands here automatically.</p>
          <Link to="/coach" className="inline-block mt-4 hero-accent px-5 py-3 rounded font-bold" data-testid="work-empty-coach">Open Coach</Link>
        </div>
      ) : (
        <div className="mt-6 grid gap-3">
          {data.works.map((w) => (
            <Link key={w.id} to={`/work/${w.id}`} className="card-panel p-5 hover:border-[#FD0F10] transition-colors block" data-testid={`work-item-${w.id}`}>
              <div className="eyebrow text-[#FD0F10]" data-testid={`work-kind-${w.id}`}>
                {label(w)} · {w.kind === "sprint" ? `${(w.done_days || []).length} of 7 days done` : `${(w.parts || []).length} saved parts`}
              </div>
              <div className="font-display text-lg font-bold mt-1">{w.title}</div>
              {w.summary && <p className="text-sm text-gray-400 mt-1 line-clamp-2">{w.summary}</p>}
              <div className="text-xs text-gray-500 mt-2">Updated {new Date(w.updated_at).toLocaleDateString()}</div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
};

const Section = ({ label, title, content, open, onSave, tid }) => {
  const [editing, setEditing] = useState(false);
  const [t, setT] = useState(title || "");
  const [c, setC] = useState(content);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await onSave(t, c); setEditing(false); toast.success("Saved"); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save. Try again."); }
    setSaving(false);
  };
  return (
    <details className="mt-3 card-panel p-5" data-testid={tid} open={open || editing}>
      <summary className="cursor-pointer font-bold">{label}</summary>
      {editing ? (
        <div className="mt-3">
          {title !== undefined && (
            <input value={t} onChange={(e) => setT(e.target.value)} className="w-full bg-[#0B0C0E] border border-[#2A2E39] rounded px-3 py-2 text-sm focus:border-[#FD0F10] outline-none" data-testid={`${tid}-title-input`} />
          )}
          <textarea value={c} onChange={(e) => setC(e.target.value)} rows={18} className="mt-2 w-full bg-[#0B0C0E] border border-[#2A2E39] rounded px-3 py-2 text-sm leading-relaxed focus:border-[#FD0F10] outline-none" data-testid={`${tid}-textarea`} />
          <div className="mt-2 flex gap-2">
            <button onClick={save} disabled={saving || !c.trim()} className="hero-accent px-4 py-2 rounded font-bold text-sm disabled:opacity-60" data-testid={`${tid}-save`}>{saving ? "Saving..." : "Save"}</button>
            <button onClick={() => { setEditing(false); setC(content); setT(title || ""); }} className="border border-[#2A2E39] px-4 py-2 rounded text-sm" data-testid={`${tid}-cancel`}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className={`${md} mt-3`}>{clean(content)}</div>
          {onSave && <button onClick={() => setEditing(true)} className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider border border-[#2A2E39] hover:border-white px-3 py-1.5 rounded" data-testid={`${tid}-edit`}><Pencil size={12} /> Edit</button>}
          <button onClick={() => navigator.clipboard.writeText(content).then(() => toast.success("Copied"), () => toast.error("Couldn't copy"))} className="mt-3 ml-2 inline-flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider border border-[#2A2E39] hover:border-white px-3 py-1.5 rounded" data-testid={`${tid}-copy`}><Copy size={12} /> Copy</button>
        </>
      )}
    </details>
  );
};

const WorkDetail = ({ id }) => {
  const [w, setW] = useState(null);
  const [err, setErr] = useState(false);
  const [dl, setDl] = useState(false);
  const download = async () => {
    setDl(true);
    try {
      const r = await api.get(`/works/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(w.title || "my-work").replace(/[^A-Za-z0-9]+/g, "-")}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Couldn't make the PDF. Try again."); }
    setDl(false);
  };
  useEffect(() => { api.get(`/works/${id}`).then((r) => setW(r.data.work)).catch(() => setErr(true)); }, [id]);
  if (err) return <p className="mt-6 text-gray-400" data-testid="work-not-found">That project wasn't found.</p>;
  if (!w) return <p className="mt-6 text-gray-400 text-sm">Loading...</p>;
  return (
    <div className="mt-4" data-testid="work-detail">
      <div className="eyebrow text-[#FD0F10]" data-testid="work-kind">{label(w)}</div>
      <h2 className="font-display text-2xl font-black mt-1" data-testid="work-title">{w.title}</h2>
      {w.summary && <p className="text-sm text-gray-300 mt-2" data-testid="work-summary">{w.summary}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Link to="/coach" className="inline-flex items-center gap-2 hero-accent px-4 py-2 rounded font-bold text-sm" data-testid="work-continue">
          <MessageSquare size={15} /> Continue with your coach
        </Link>
        <button onClick={download} disabled={dl} className="inline-flex items-center gap-2 border border-[#2A2E39] hover:border-white px-4 py-2 rounded font-bold text-sm disabled:opacity-60" data-testid="work-download-pdf">
          <Download size={15} /> {dl ? "Making PDF..." : "Download PDF"}
        </button>
      </div>
      <div className="mt-3" />
      {w.outline && (
        <Section label="Outline" content={w.outline} open={!w.parts?.length} tid="work-outline"
          onSave={async (_t, c) => { await api.put(`/works/${id}/outline`, { content: c }); setW((x) => ({ ...x, outline: c })); }} />
      )}
      {w.kind === "sprint" && (
        <p className="text-sm text-gray-300 mb-2" data-testid="work-sprint-status">
          {(w.done_days || []).length} of 7 days done. {w.active ? <Link to={`/opportunity/${w.opportunity_id}`} className="text-[#FD0F10] underline" data-testid="work-sprint-open-path">Tick off days on the path page</Link> : "This sprint is saved. Start that path again to make it active."}
        </p>
      )}
      {(w.parts || []).map((p) => (
        <Section key={p.n} label={w.kind === "sprint" ? `${(w.done_days || []).includes(p.n) ? "✓ " : ""}${p.title}` : `${p.n}. ${p.title}`} title={p.title} content={p.content} tid={`work-part-${p.n}`}
          onSave={w.kind === "sprint" ? undefined : async (t, c) => {
            await api.put(`/works/${id}/parts/${p.n}`, { title: t, content: c });
            setW((x) => ({ ...x, parts: x.parts.map((q) => (q.n === p.n ? { ...q, title: t.trim() || q.title, content: c } : q)) }));
          }} />
      ))}
      {w.kind !== "sprint" && <p className="mt-4 text-xs text-gray-400">Want a bigger rewrite? Ask your coach, for example "Rewrite chapter 2 to be more direct." The new version saves here automatically.</p>}
    </div>
  );
};

export default function MyWork() {
  const { id } = useParams();
  return (
    <div className="max-w-3xl mx-auto px-5 py-8" data-testid="my-work-page">
      {id ? (
        <Link to="/work" className="text-xs font-mono-ui uppercase tracking-wider text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="work-back"><ArrowLeft size={13} /> My Work</Link>
      ) : (
        <>
          <div className="eyebrow text-[#FD0F10] flex items-center gap-2"><FolderOpen size={14} /> MY WORK</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black mt-1">Everything you've built</h1>
          <p className="text-sm text-gray-400 mt-2">Ebooks, outlines, drafts and plans you made with your coach save here automatically.</p>
          <WorkExtras />
        </>
      )}
      {id ? <WorkDetail id={id} /> : <WorkList />}
    </div>
  );
}
